import { useEffect, useRef, useState } from "react";
import { CheckoutQuestionStep } from "./CheckoutQuestionStep";
import { Text } from "~/components/Text";

const CANVAS_SIZE = 768;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

type Point = { x: number; y: number };
type ImageSize = { width: number; height: number };

type Props = {
  file: File | string;
  onSaveExisting?: () => Promise<void>;
  loading: boolean;
  uploadError: string | null;
  onReplace: () => void;
  onBack: () => void;
  onConfirm: (file: File) => Promise<void>;
};

export function ProfilePhotoCropper({
  file,
  onSaveExisting,
  loading,
  uploadError,
  onReplace,
  onBack,
  onConfirm,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const pointersRef = useRef(new Map<number, Point>());
  const tapRef = useRef<Point | null>(null);
  const suppressClickRef = useRef(false);
  const dragRef = useRef<{ point: Point; position: Point } | null>(null);

  const [imageSize, setImageSize] = useState<ImageSize | null>(null);
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setImageSize(null);
    setPosition({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setError(null);
    const objectUrl = typeof file === "string" ? file : URL.createObjectURL(file);
    const image = new Image();
    if (typeof file === "string") image.crossOrigin = "anonymous";
    image.onload = () => {
      imageRef.current = image;
      setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => setError("We couldn't open that image. Try another one.");
    image.src = objectUrl;

    return () => {
      image.onload = null;
      image.onerror = null;
      imageRef.current = null;
      if (typeof file !== "string") URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !imageSize) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const baseScale = Math.max(
      CANVAS_SIZE / imageSize.width,
      CANVAS_SIZE / imageSize.height,
    );
    const scale = baseScale * zoom;
    const width = imageSize.width * scale;
    const height = imageSize.height * scale;
    const maxOffsetX = Math.max(0, (width - CANVAS_SIZE) / 2);
    const maxOffsetY = Math.max(0, (height - CANVAS_SIZE) / 2);
    const x = (CANVAS_SIZE - width) / 2 + position.x * maxOffsetX;
    const y = (CANVAS_SIZE - height) / 2 + position.y * maxOffsetY;

    context.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    context.drawImage(image, x, y, width, height);
  }, [imageSize, position, zoom]);


  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (loading) return;
    if (pointersRef.current.size === 0) {
      tapRef.current = { x: event.clientX, y: event.clientY };
      suppressClickRef.current = false;
    } else {
      suppressClickRef.current = true;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    if (pointersRef.current.size === 1) {
      dragRef.current = {
        point: { x: event.clientX, y: event.clientY },
        position,
      };
    } else if (pointersRef.current.size === 2) {
      dragRef.current = null;

    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    if (tapRef.current && Math.hypot(event.clientX - tapRef.current.x, event.clientY - tapRef.current.y) > 5) {
      suppressClickRef.current = true;
    }
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });


    if (pointersRef.current.size !== 1 || !dragRef.current || !imageSize) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const baseScale = Math.max(
      CANVAS_SIZE / imageSize.width,
      CANVAS_SIZE / imageSize.height,
    );
    const width = imageSize.width * baseScale * zoom;
    const height = imageSize.height * baseScale * zoom;
    const maxOffsetX = Math.max(0, (width - CANVAS_SIZE) / 2);
    const maxOffsetY = Math.max(0, (height - CANVAS_SIZE) / 2);
    const canvasScale = CANVAS_SIZE / rect.width;
    const deltaX = (event.clientX - dragRef.current.point.x) * canvasScale;
    const deltaY = (event.clientY - dragRef.current.point.y) * canvasScale;

    setPosition({
      x:
        maxOffsetX === 0
          ? 0
          : Math.max(
              -1,
              Math.min(1, dragRef.current.position.x + deltaX / maxOffsetX),
            ),
      y:
        maxOffsetY === 0
          ? 0
          : Math.max(
              -1,
              Math.min(1, dragRef.current.position.y + deltaY / maxOffsetY),
            ),
    });
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.type === "pointercancel") suppressClickRef.current = true;
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size === 1) {
      const point = [...pointersRef.current.values()][0];
      dragRef.current = { point, position };

      return;
    }
    dragRef.current = null;

  };

  const confirm = async () => {
    if (loading) return;
    if (onSaveExisting && zoom === MIN_ZOOM && position.x === 0 && position.y === 0) {
      await onSaveExisting();
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas || !imageSize) return;

    setError(null);
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) =>
            result ? resolve(result) : reject(new Error("Image crop failed.")),
          "image/jpeg",
          0.9,
        );
      });
      await onConfirm(
        new File([blob], "butter-profile-photo.jpg", { type: "image/jpeg" }),
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "We couldn't crop that image. Please try again.",
      );
    }
  };

  return (
    <CheckoutQuestionStep
      title="Add a photo"
      description="One where it’s clearly you. Group pics don’t count."
      onSubmit={confirm}
      onBack={onBack}
      submitLabel="Next"
      submitDisabled={!imageSize && !onSaveExisting}
      loading={loading}
      error={error || uploadError}
    >
      <div className="flex flex-col gap-5 sm:min-h-[440px]">
      <button
        type="button"
        aria-label="Replace profile photo"
        disabled={loading}
        onClick={event => {
          if (event.detail === 0 || !suppressClickRef.current) onReplace();
        }}
        className="relative mx-auto aspect-square w-3/5 overflow-hidden rounded-3xl bg-butter-green/10"
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          aria-label="Profile photo crop preview"
          className="h-full w-full touch-none cursor-move"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-3xl border-2 border-white/80 shadow-[inset_0_0_0_999px_rgba(74,80,67,0.08)]"
        />
      </button>

      <label className="flex flex-col gap-2 font-sans text-sm font-bold text-butter-green">
        Zoom
        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step="0.01"
          disabled={loading || !imageSize}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          className="w-full accent-butter-pink"
        />
      </label>

      <Text as="p" variant="secondary" size="sm" className="text-center">
        Tap the photo to replace it. Drag to reposition. Use the slider to zoom.
      </Text>

      </div>
    </CheckoutQuestionStep>
  );
}
